import { Controller } from "@hotwired/stimulus"

// beforeinstallprompt llega UNA vez por carga completa; el header del partner se
// re-renderiza en cada visita Turbo, así que el evento se guarda a nivel de módulo y no
// en la instancia (si no, el ítem desaparecía tras la primera navegación).
let deferredPrompt = null

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault()
  deferredPrompt = event
  window.dispatchEvent(new CustomEvent("install-pwa:available"))
})

window.addEventListener("appinstalled", () => {
  deferredPrompt = null
  window.dispatchEvent(new CustomEvent("install-pwa:installed"))
})

// Ítem "Instalar aplicación" del menú del partner + sheet con instrucciones.
// Montado en el <header> de nav_partners (dropdown y sheet comparten controller).
// Connects to data-controller="install-pwa". Ver _docs/frontend/pwa.md
export default class extends Controller {
  static targets = ["button", "modal", "instructions", "modalInstall"]

  connect() {
    if (this.isStandalone()) {
      this.removeButton()
      return
    }

    this.onAvailable = () => this.showButton()
    this.onInstalled = () => { this.close(); this.removeButton() }
    window.addEventListener("install-pwa:available", this.onAvailable)
    window.addEventListener("install-pwa:installed", this.onInstalled)

    // Chrome/Edge: ya llegó el evento (visita Turbo posterior) o llegará (install-pwa:available).
    // Safari (iPhone/iPad) no lo dispara nunca: el ítem abre instrucciones.
    if (deferredPrompt || this.detectDeviceAndBrowser().device === "iOS") this.showButton()
  }

  disconnect() {
    window.removeEventListener("install-pwa:available", this.onAvailable)
    window.removeEventListener("install-pwa:installed", this.onInstalled)
  }

  open(event) {
    event?.preventDefault()
    if (!this.hasModalTarget) return

    const canPrompt = Boolean(deferredPrompt)

    if (this.hasInstructionsTarget) {
      this.instructionsTarget.innerHTML = canPrompt ? "" : this.instructionsHtml()
      this.instructionsTarget.hidden = canPrompt
    }
    if (this.hasModalInstallTarget) this.modalInstallTarget.hidden = !canPrompt

    this.modalTarget.style.display = "block"
  }

  close() {
    if (this.hasModalTarget) this.modalTarget.style.display = "none"
  }

  async install() {
    if (!deferredPrompt) return

    const prompt = deferredPrompt
    deferredPrompt = null
    prompt.prompt()
    const { outcome } = await prompt.userChoice
    this.close()
    // Rechazado: Chrome no vuelve a disparar el evento en esta carga; el ítem se va
    // hasta la próxima carga completa. Aceptado: appinstalled lo quita de todos modos.
    if (outcome !== "accepted") this.removeButton()
  }

  // --- helpers ---

  isStandalone() {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
  }

  showButton() {
    if (this.hasButtonTarget) this.buttonTarget.hidden = false
  }

  removeButton() {
    if (this.hasButtonTarget) this.buttonTarget.remove()
  }

  instructionsHtml() {
    const { device, browser } = this.detectDeviceAndBrowser()
    const share = '<span class="material-icons-outlined instruction-icon">ios_share</span>'
    const add = '<span class="material-icons-outlined instruction-icon">add_box</span>'

    if (device === "iOS" && browser === "Safari") {
      return `
        <li class="mb-2">Para instalar desde Safari:</li>
        <li class="mb-1">1. Pulsa <strong>Compartir</strong> ${share} en la barra de Safari.</li>
        <li>2. Elige ${add} <strong>Agregar a pantalla de inicio</strong> (en iPad también <strong>Añadir al Dock</strong>).</li>
      `
    }
    if (device === "iOS" && browser === "Chrome") {
      return `
        <li class="mb-2">Para instalar desde Chrome en iOS:</li>
        <li class="mb-1">1. Pulsa <strong>Compartir</strong> ${share} en la esquina superior derecha.</li>
        <li>2. Elige ${add} <strong>Añadir a pantalla de inicio</strong>.</li>
      `
    }
    if (device === "Android" && (browser === "Chrome" || browser === "Edge")) {
      return `
        <li class="mb-2">Para instalar desde ${browser} en Android:</li>
        <li class="mb-1">1. Toca el menú <strong>⋮</strong> en la esquina superior derecha.</li>
        <li>2. Elige <strong>Instalar aplicación</strong> o <strong>Añadir a la pantalla de inicio</strong>.</li>
      `
    }
    if (device === "Desktop" && (browser === "Chrome" || browser === "Edge")) {
      return `
        <li class="mb-2">Para instalar desde ${browser}:</li>
        <li class="mb-1">1. Haz clic en el ícono de instalar al final de la barra de direcciones, o abre el menú <strong>⋮</strong>.</li>
        <li>2. Elige <strong>Instalar Greatfood</strong>.</li>
      `
    }
    if (browser === "Firefox") {
      return `
        <li class="mb-2">Firefox no permite instalar aplicaciones web.</li>
        <li>Abre esta misma dirección en <strong>Chrome</strong> o <strong>Edge</strong> y vuelve a este menú.</li>
      `
    }
    return `
      <li class="mb-2">Este navegador no ofrece la instalación de forma directa.</li>
      <li>Abre esta misma dirección en <strong>Chrome</strong>, <strong>Edge</strong> o <strong>Safari</strong> y vuelve a este menú.</li>
    `
  }

  detectDeviceAndBrowser() {
    const ua = navigator.userAgent
    // iPadOS se presenta como Macintosh; lo delata el touch.
    const isIpadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1

    let device = "Desktop"
    if (/android/i.test(ua)) device = "Android"
    else if (/iPad|iPhone|iPod/.test(ua) || isIpadOS) device = "iOS"

    let browser = "Otro"
    if (/firefox|fxios/i.test(ua)) browser = "Firefox"
    else if (/edg\//i.test(ua)) browser = "Edge"
    else if (/chrome|crios/i.test(ua)) browser = "Chrome"
    else if (/safari/i.test(ua)) browser = "Safari"

    return { device, browser }
  }
}
